import { Close, MoreHoriz, Search } from "@mui/icons-material";
import { Box, Button, Dialog, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Camera, User } from "../types";
import UserDeleteModal from "./UserDeleteModal";
import UserEditModal from "./UserEditModal";

interface UserTableProps {
    users: User[];
    cameras: Camera[];
    updateUser: (u: User) => void;
    deleteUser: (id: string) => void;
    loading: { deleteUser: Map<string, boolean>, updateUser: boolean };
}

export default function ManageUserTable({ users, cameras, updateUser, deleteUser, loading }: UserTableProps) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0);
    }

    const filteredCameraId = useMemo<Set<string>>(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return new Set();

        return new Set(cameras
            .filter(cam => cam.name.toLowerCase().includes(term))
            .map(cam => cam.id))
    }, [cameras, searchTerm])

    const filteredEntries = useMemo<User[]>(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return users;

        return users.filter((user) => {
            const emailMatches = user.email.toLowerCase().includes(term);
            const notesMatches = user.notes?.toLowerCase().includes(term) ?? false;
            const cameraMatches = user.camera_ids && user.camera_ids.some((id) => filteredCameraId.has(id));

            return emailMatches || notesMatches || cameraMatches;
        });
    }, [users, filteredCameraId, searchTerm])

    const pagedEntries = filteredEntries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage);

    // notes overflow check
    const noteRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [overflowNotes, setOverflowNotes] = useState<Set<string>>(new Set());

    useEffect(() => {
        const newSet = new Set<string>();
        pagedEntries.forEach(user => {
            const element = noteRefs.current[user.id];
            if (element && element.scrollHeight > element.clientHeight) {
                newSet.add(user.id);
            }
        });
        setOverflowNotes(newSet);
    }, [searchTerm, page, rowsPerPage, users, cameras])

    // notes expand modal
    const [openNote, setOpenNote] = useState(false);
    const [currentNote, setCurrentNote] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');

    const handleOpenNote = (note: string, email: string) => {
        setCurrentEmail(email);
        setCurrentNote(note);
        setOpenNote(true);
    };
    const handleCloseNote = () => setOpenNote(false);

    // User edit Modal
    const [userToEdit, setUserToEdit] = useState<User | null>(null);
    const [userEditDialogOpen, setuserEditDialogOpen] = useState(false);

    const handleEditClick = (user: User) => {
        setUserToEdit(user);
        setuserEditDialogOpen(true);
    }

    const handleEditDialogClose = () => {
        setuserEditDialogOpen(false);
        setUserToEdit(null);
    }

    // edit confirm in UserEditModal.tsx

    // User delete Modal
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [userDeleteDialogOpen, setuserDeleteDialogOpen] = useState(false);

    const handleDeleteClick = (user: User) => {
        setUserToDelete(user);
        setuserDeleteDialogOpen(true);
    }

    const handleDeleteDialogClose = () => {
        setuserDeleteDialogOpen(false);
        setUserToDelete(null);
    }

    const handleDeleteConfirm = () => {
        if (!userToDelete || typeof userToDelete.id !== 'string') return;
        deleteUser(userToDelete.id);
        handleDeleteDialogClose();
    }

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TextField
                slotProps={{
                    input: {
                        startAdornment: <InputAdornment
                            position="start"
                            sx={{
                                color: 'inherit',
                                mr: 1
                            }}>
                            <Search />
                        </InputAdornment>
                    }
                }}
                fullWidth
                sx={{
                    maxWidth: '95%',
                    display: 'flex',
                    mx: 'auto'
                }}
                variant="outlined"
                label="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                margin="normal">
            </TextField>
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Camera</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {pagedEntries.map((user) => {
                            return (
                                <TableRow key={user.id}>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.role}</TableCell>
                                    <TableCell>
                                        {user.role === 'admin'
                                            ? 'All Cameras'
                                            : user.camera_ids && user.camera_ids.length > 0
                                                ? user.camera_ids.map((id) => cameras.find((item) => item.id === id)?.name || id)
                                                    .join(', ')
                                                : 'None'}
                                    </TableCell>
                                    <TableCell sx={{
                                        width: '40%'
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}>
                                            <Box
                                                ref={(element: HTMLDivElement | null) => { noteRefs.current[user.id] = element }}
                                                sx={{
                                                    flex: 1,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}>
                                                {user.notes || '-'}
                                            </Box>

                                            {/* overflow notes expand modal */}
                                            {overflowNotes.has(user.id) && (
                                                <IconButton
                                                    aria-label="expand note"
                                                    onClick={() => handleOpenNote(user.notes || '', user.email)}
                                                    sx={{
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'inherit',
                                                        ml: 1
                                                    }}>
                                                    <MoreHoriz />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button onClick={() => handleEditClick(user)}>Edit</Button>
                                        <Button
                                            onClick={() => handleDeleteClick(user)}
                                            disabled={users.length <= 1}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                        {pagedEntries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No users found.</TableCell>
                            </TableRow>
                        )}

                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredEntries.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />

            {/* Notes Modal */}
            <Dialog open={openNote} onClose={handleCloseNote}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                    }}>

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            display: 'flex',
                        }}>
                        Note: {currentEmail}
                    </Typography>
                    <IconButton
                        aria-label="close dialog"
                        onClick={handleCloseNote}
                        sx={{ color: 'inherit' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <DialogContentText whiteSpace="pre-wrap">{currentNote}</DialogContentText>
                </DialogContent>
            </Dialog>

            {/* User Edit Modal */}
            <UserEditModal
                open={userEditDialogOpen}
                handleClose={handleEditDialogClose}
                editingUser={userToEdit}
                cameras={cameras}
                loadingUpdateUser={loading.updateUser}
                setEditingUser={setUserToEdit}
                updateUser={updateUser}
            />

            {/* User Delete Modal */}
            {userToDelete && (
                <UserDeleteModal
                    open={userDeleteDialogOpen}
                    user={userToDelete}
                    loading={loading.deleteUser.get(userToDelete.id) || false}
                    handleClose={handleDeleteDialogClose}
                    onConfirm={handleDeleteConfirm} />
            )}
        </Paper >
    )
}